<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const props = defineProps({
  channel: { type: String, default: 'all' },
});

const route = useRoute();
const accountId = route.params.accountId;
const data = ref({});
const loading = ref(true);

const load = async () => {
  loading.value = true;
  try {
    const p = new URLSearchParams();
    if (props.channel !== 'all') p.set('channel', props.channel);
    data.value = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances/cashflow?${p.toString()}`
    );
  } catch (e) {
    data.value = {};
  }
  loading.value = false;
};
watch(() => props.channel, load);
onMounted(load);

const moneyFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const money = v => `${moneyFmt.format(Number(v) || 0)} ₺`;

const summary = computed(() => data.value.summary || {});
const monthly = computed(() => data.value.monthly || []);
const upcoming = computed(() => data.value.upcoming || []);
const batches = computed(() => data.value.batches || []);
const byChannel = computed(() => data.value.by_channel || []);
const maxMonthly = computed(() =>
  Math.max(
    1,
    ...monthly.value.flatMap(m => [Number(m.paid), Number(m.accrued)])
  )
);
const maxUpcoming = computed(() =>
  Math.max(1, ...upcoming.value.map(u => Number(u.amount)))
);

const monthLabel = ym => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return new Date(y, m - 1, 1).toLocaleDateString('tr-TR', {
    month: 'long',
    year: 'numeric',
  });
};
const fmtDay = iso =>
  iso
    ? new Date(iso).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';
const pct = (v, max) => `${Math.max(2, Math.round((Number(v) / max) * 100))}%`;
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="loading" class="py-8 text-center text-n-slate-10">
      Nakit akışı hesaplanıyor...
    </div>

    <template v-else>
      <!-- Akıllı özet -->
      <div
        v-if="data.smart"
        class="flex gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-900/40"
      >
        <span class="i-lucide-wallet-cards text-2xl text-blue-600 shrink-0" />
        <p class="text-sm leading-relaxed text-n-slate-12">{{ data.smart }}</p>
      </div>

      <!-- KPI kartları -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div
          class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-[11px] text-n-slate-10">Toplam Yatan</span>
          <span
            class="text-lg font-bold tabular-nums text-green-700 dark:text-green-400"
            >{{ money(summary.total_paid) }}</span
          >
          <span class="text-[11px] text-n-slate-10"
            >{{ summary.paid_count }} sipariş</span
          >
        </div>
        <div
          class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-[11px] text-n-slate-10">Bekleyen (yolda)</span>
          <span
            class="text-lg font-bold tabular-nums text-blue-700 dark:text-blue-400"
            >{{ money(summary.total_pending) }}</span
          >
          <span class="text-[11px] text-n-slate-10"
            >{{ summary.pending_count }} sipariş</span
          >
        </div>
        <div
          class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-[11px] text-n-slate-10">Bu Ay Yatan</span>
          <span class="text-lg font-bold tabular-nums text-n-slate-12">{{
            money(summary.this_month_paid)
          }}</span>
        </div>
        <div
          class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-[11px] text-n-slate-10">Gelecek 30 Gün</span>
          <span class="text-lg font-bold tabular-nums text-n-slate-12">{{
            money(summary.next_30)
          }}</span>
          <span class="text-[11px] text-n-slate-10">yaklaşan tahsilat</span>
        </div>
        <div
          class="flex flex-col p-3 rounded-xl border"
          :class="
            summary.stuck_count > 0
              ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-300 dark:border-amber-900/50'
              : 'bg-n-solid-2 border-n-weak'
          "
        >
          <span class="text-[11px] text-n-slate-10">Takılmış Ödeme</span>
          <span
            class="text-lg font-bold tabular-nums"
            :class="
              summary.stuck_count > 0
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-n-slate-12'
            "
            >{{ money(summary.stuck_amount) }}</span
          >
          <span class="text-[11px] text-n-slate-10"
            >{{ summary.stuck_count }} sipariş gecikti</span
          >
        </div>
        <div
          class="flex flex-col p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-[11px] text-n-slate-10">Ort. Tahsilat</span>
          <span class="text-lg font-bold tabular-nums text-n-slate-12"
            >{{ summary.avg_collection_days }} gün</span
          >
          <span class="text-[11px] text-n-slate-10">sipariş → hesap</span>
        </div>
      </div>

      <!-- Mağaza kırılımı -->
      <div v-if="byChannel.length > 1" class="grid grid-cols-2 gap-3">
        <div
          v-for="c in byChannel"
          :key="c.channel"
          class="flex items-center justify-between p-3 rounded-xl bg-n-solid-2 border border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">{{ c.label }}</span>
          <div class="text-right">
            <div
              class="text-sm font-semibold tabular-nums text-green-700 dark:text-green-400"
            >
              {{ money(c.paid) }}
              <span class="text-[11px] text-n-slate-10">yatan</span>
            </div>
            <div
              class="text-[11px] tabular-nums text-blue-700 dark:text-blue-400"
            >
              {{ money(c.pending) }} yolda
            </div>
          </div>
        </div>
      </div>

      <!-- Aylık nakit akışı: yatan vs satış -->
      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          Aylık Nakit Akışı — Yatan (hesaba giren) vs Satış (o ay tahakkuk)
        </h3>
        <p class="text-[11px] text-n-slate-10 -mt-1">
          Her ay sattığından az yatar; çünkü o ayın satışı ~28 gün sonra
          (genelde ertesi ay) hesaba geçer. "Ocak'ta yatan Şubat satışı"
          gerçeği.
        </p>
        <div class="flex flex-col gap-2.5 mt-1">
          <div
            v-for="m in monthly"
            :key="m.month"
            class="flex items-center gap-3"
          >
            <span class="w-24 text-xs text-n-slate-11 shrink-0">{{
              monthLabel(m.month)
            }}</span>
            <div class="flex flex-col flex-1 gap-1">
              <div class="flex items-center gap-2">
                <div
                  class="flex-1 h-3 rounded-full bg-n-alpha-black1 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-green-500"
                    :style="{ width: pct(m.paid, maxMonthly) }"
                  />
                </div>
                <span
                  class="w-28 text-xs text-right tabular-nums text-green-700 dark:text-green-400 shrink-0"
                  >{{ money(m.paid) }}</span
                >
              </div>
              <div class="flex items-center gap-2">
                <div
                  class="flex-1 h-3 rounded-full bg-n-alpha-black1 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-n-slate-8"
                    :style="{ width: pct(m.accrued, maxMonthly) }"
                  />
                </div>
                <span
                  class="w-28 text-xs text-right tabular-nums text-n-slate-11 shrink-0"
                  >{{ money(m.accrued) }}</span
                >
              </div>
            </div>
          </div>
        </div>
        <div class="flex gap-4 mt-1 text-[11px] text-n-slate-10">
          <span class="flex items-center gap-1"
            ><span class="w-3 h-2 rounded-full bg-green-500" /> Yatan
            (nakit)</span
          >
          <span class="flex items-center gap-1"
            ><span class="w-3 h-2 rounded-full bg-n-slate-8" /> Satış
            (tahakkuk)</span
          >
        </div>
      </div>

      <!-- Gelecek hakediş takvimi -->
      <div v-if="upcoming.length" class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          Gelecek Hakediş Takvimi — Yolda olan para ne zaman geliyor
        </h3>
        <div class="flex flex-col gap-2 mt-1">
          <div
            v-for="u in upcoming"
            :key="u.month"
            class="flex items-center gap-3"
          >
            <span class="w-24 text-xs text-n-slate-11 shrink-0">{{
              monthLabel(u.month)
            }}</span>
            <div
              class="flex-1 h-4 rounded-full bg-n-alpha-black1 overflow-hidden"
            >
              <div
                class="h-full rounded-full bg-blue-500"
                :style="{ width: pct(u.amount, maxUpcoming) }"
              />
            </div>
            <span
              class="w-28 text-xs text-right tabular-nums text-blue-700 dark:text-blue-400 shrink-0"
              >{{ money(u.amount) }}</span
            >
            <span class="w-16 text-[11px] text-right text-n-slate-10 shrink-0"
              >{{ u.count }} sip.</span
            >
          </div>
        </div>
      </div>

      <!-- Ödeme partileri = banka transferleri -->
      <div v-if="batches.length" class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          Son Ödeme Partileri (Banka Transferleri)
        </h3>
        <p class="text-[11px] text-n-slate-10 -mt-1">
          Trendyol her ödemede birçok siparişi tek transferle yatırır. Bu
          tutarları banka ekstrendeki para girişleriyle karşılaştırabilirsin.
        </p>
        <div class="overflow-x-auto rounded-xl border border-n-weak mt-1">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2 font-medium text-left">Ödeme Tarihi</th>
                <th class="px-3 py-2 font-medium text-left">Dekont No</th>
                <th class="px-3 py-2 font-medium text-right">Tutar</th>
                <th class="px-3 py-2 font-medium text-right">Sipariş</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="b in batches"
                :key="b.payment_order_id"
                class="border-t border-n-weak"
              >
                <td class="px-3 py-2 whitespace-nowrap text-n-slate-12">
                  {{ fmtDay(b.date) }}
                </td>
                <td class="px-3 py-2 text-n-slate-10 tabular-nums">
                  {{ b.payment_order_id }}
                </td>
                <td
                  class="px-3 py-2 text-right tabular-nums font-medium text-green-700 dark:text-green-400"
                >
                  {{ money(b.amount) }}
                </td>
                <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                  {{ b.count }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
